import { Button, Form, FormControl, FormField, FormItem, FormMessage, Input } from '@cibli/shared';
import { BoothOtpMode } from '@cibli/shared';
import { ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '@/features/common/components/IconButton.tsx';
import MainLayout from '@/features/common/components/MainLayout';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { featureFlagsService } from '@/services/featureFlags';

import type { UserInfo } from '../context/register.context';

import FormButtons from '../components/FormButtons';
import { useRegisterContext } from '../hooks/useRegisterContext';

interface Props {
  onCancel: () => void;
  onBack?: () => void;
  onNext: (data?: Partial<UserInfo>) => void | Promise<void>;
}

export default function NamesScreen({ onCancel, onBack, onNext }: Props) {
  const { t } = useTranslation();
  const { booth } = useBooth();
  const { namesForm, cguForm } = useRegisterContext();
  const [isFTConnectEnabled, setIsFTConnectEnabled] = useState(false);

  // Email is mandatory when the booth verifies candidates by email
  const emailRequired = booth?.otpMode === BoothOtpMode.EMAIL;

  useEffect(() => {
    featureFlagsService.getFranceTravailConnectFlag().then(setIsFTConnectEnabled);
  }, []);

  return (
    <MainLayout
      className="gap-4 sm:gap-6 text-center"
      onClose={onCancel}
      rightContent={
        onBack ? (
          <IconButton
            className="ml-6"
            icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
            onClick={onBack}
          />
        ) : undefined
      }
    >
      <h3 className="text-lg sm:text-xl text-primary">{t('FormScreen.Names.hint')}</h3>
      <h1 className="text-3xl md:text-4xl lg:text-5xl text-balance">
        {t('FormScreen.Names.title')}
      </h1>
      <p className="text-lg md:text-xl lg:text-2xl max-w-xl text-balance text-muted-foreground">
        {t('FormScreen.Names.description')}
      </p>

      <Form {...namesForm}>
        <form
          onSubmit={namesForm.handleSubmit(() => {
            void onNext({ ...namesForm.getValues(), ...cguForm.getValues() });
          })}
          className="w-full flex flex-col gap-4 max-w-md"
        >
          <FormField
            control={namesForm.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormControl>
                  <Input
                    size="xl"
                    autoFocus
                    placeholder={t('FormScreen.Names.firstName')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={namesForm.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input size="xl" placeholder={t('FormScreen.Names.lastName')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={namesForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    size="xl"
                    placeholder={
                      emailRequired
                        ? t('FormScreen.Names.emailRequired')
                        : t('FormScreen.Names.email')
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isFTConnectEnabled && (
            <div className="flex flex-col gap-2 mt-2">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">{t('common.or')}</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => {
                  // TODO: Implémenter le flow OAuth France Travail Connect
                  // 1. Rediriger vers l'URL d'autorisation France Travail
                  // 2. Récupérer le code d'autorisation depuis le callback
                  // 3. Échanger le code contre un access token
                  // 4. Appeler l'API userinfo pour récupérer les infos utilisateur
                  // 5. Pré-remplir le formulaire avec les données récupérées
                  console.log('France Travail Connect - À implémenter');
                }}
              >
                {t('FormScreen.Names.franceTravailConnect')}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                {t('FormScreen.Names.franceTravailConnectDescription')}
              </p>
            </div>
          )}

          <FormButtons showBackButton={!!onBack} onBack={onBack} stepName="names" />
        </form>
      </Form>
    </MainLayout>
  );
}
